import { useCallback, useRef, useState } from "react";

// Module-level cache: survives component remounts within the same session.
// Key: "voice:text", Value: object URL pointing to a blob
const audioCache = new Map<string, string>();

type AudioButtonProps = {
  text: string;
  voice?: "nova" | "alloy" | "echo" | "fable" | "onyx" | "shimmer";
  /** visual size variant */
  size?: "sm" | "md";
  className?: string;
};

type Status = "idle" | "loading" | "playing" | "error";

export function AudioButton({
  text,
  voice = "nova",
  size = "md",
  className = "",
}: AudioButtonProps) {
  const [status, setStatus] = useState<Status>("idle");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const play = useCallback(async () => {
    if (status === "loading") return;

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    const cacheKey = `${voice}:${text}`;
    let objectUrl = audioCache.get(cacheKey);

    if (!objectUrl) {
      setStatus("loading");
      try {
        const url = `/api/tts?text=${encodeURIComponent(text)}&voice=${voice}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`TTS request failed: ${res.status}`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        audioCache.set(cacheKey, objectUrl);
      } catch (err) {
        console.error("[AudioButton] TTS error:", err);
        setStatus("error");
        // Reset to idle after a moment so user can retry
        setTimeout(() => setStatus("idle"), 2000);
        return;
      }
    }

    const audio = new Audio(objectUrl);
    audioRef.current = audio;
    setStatus("playing");

    audio.onended = () => setStatus("idle");
    audio.onerror = () => {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 2000);
    };

    try {
      await audio.play();
    } catch {
      setStatus("idle");
    }
  }, [text, voice, status]);

  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  const buttonSize = size === "sm"
    ? "w-6 h-6 rounded-md"
    : "w-8 h-8 rounded-lg";

  return (
    <button
      type="button"
      onClick={play}
      disabled={status === "loading"}
      title="Play pronunciation"
      aria-label="Play pronunciation"
      className={[
        "inline-flex items-center justify-center shrink-0 transition-all",
        buttonSize,
        status === "playing"
          ? "bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
          : status === "error"
          ? "bg-red-100 dark:bg-red-900/40 text-red-500"
          : status === "loading"
          ? "bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-wait"
          : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400",
        className,
      ].join(" ")}
    >
      {status === "loading" ? (
        // Spinner
        <svg className={`${iconSize} animate-spin`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      ) : status === "playing" ? (
        // Sound waves icon
        <svg className={iconSize} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 4a1 1 0 00-1.447-.894l-4 2A1 1 0 001 6v8a1 1 0 00.553.894l4 2A1 1 0 007 16V4z" />
          <path d="M9.293 5.293a1 1 0 011.414 0A8.001 8.001 0 0121 10a8.001 8.001 0 01-10.293 4.707 1 1 0 01-1.414-1.414A6 6 0 0019 10a6 6 0 00-9.707-4.293 1 1 0 010-1.414z" />
          <path d="M11.414 7.586a1 1 0 011.414 0A4 4 0 0117 10a4 4 0 01-4.172 3.999 1 1 0 01-.828-1.619A2 2 0 0015 10a2 2 0 00-2.172-1.999 1 1 0 01-.414-1.415z" />
        </svg>
      ) : status === "error" ? (
        // X icon
        <svg className={iconSize} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
        </svg>
      ) : (
        // Speaker icon (idle)
        <svg className={iconSize} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 4a1 1 0 00-1.447-.894l-4 2A1 1 0 001 6v8a1 1 0 00.553.894l4 2A1 1 0 007 16V4zM9.553 4.168A1 1 0 0011 5.073V14.927a1 1 0 01-1.447.894l-.553-.277V5.447l.553-.279zM12.5 6.5a1 1 0 011 1v5a1 1 0 01-2 0v-5a1 1 0 011-1z" />
        </svg>
      )}
    </button>
  );
}
